import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import toast from "react-hot-toast";
import { useUpdateProductMutation } from "../Redux/api/productApi";
import { useUploadFileMutation } from "../Redux/api/mediaApi";
import { editProductSchema } from "../Validations/productValidation";
import { ImagePlus } from "lucide-react";
import { motion } from "framer-motion";

const EditProductForm = ({ productData, onClose, onUpdated }) => {
  const [previewImage, setPreviewImage] = useState(null);
  const [updateProduct, { isLoading: isUpdating }] = useUpdateProductMutation();
  const [uploadFile] = useUploadFileMutation();

  const {
    register,
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(editProductSchema),
    defaultValues: {
      name: "",
      description: "",
      category_id: "",
      base_price: "",
      stock_quantity: "",
      min_order_quantity: "",
    },
  });

  useEffect(() => {
    if (productData) {
      setValue("name", productData.name || "");
      setValue("description", productData.description || "");
      setValue("category_id", productData.category_id?.toString() || "");
      setValue("base_price", productData.base_price || "");
      setValue("stock_quantity", productData.stock_quantity || "");
      setValue("min_order_quantity", productData.min_order_quantity || "");
      if (productData.image_url) setPreviewImage(productData.image_url);
    }
  }, [productData, setValue]);

  const categories = [
    { id: 1, name: "Apparel & Accessories" },
    { id: 2, name: "Furniture" },
  ];

  const onSubmit = async (data) => {
    const toastId = toast.loading("Updating product...");
    try {
      let fileIds = productData.file_ids || [];

      // if user selects new image(s), upload them
      if (data.files && data.files.length > 0) {
        const uploadedIds = [];
        for (let i = 0; i < data.files.length; i++) {
          const res = await uploadFile(data.files[i]).unwrap();
          if (res?.id) uploadedIds.push(res.id);
        }
        fileIds = uploadedIds;
      }

      const payload = {
        id: productData.id,
        name: data.name,
        description: data.description,
        category_id: Number(data.category_id),
        base_price: Number(data.base_price),
        stock_quantity: Number(data.stock_quantity),
        min_order_quantity: Number(data.min_order_quantity),
        file_ids: fileIds,
      };

      const result = await updateProduct(payload).unwrap();

      toast.dismiss(toastId);
      toast.success("Product updated successfully!");
      onUpdated && onUpdated(result);
      onClose && onClose();
    } catch (err) {
      toast.dismiss(toastId);
      console.error("Update error:", err);
      toast.error(err?.data?.message || "Failed to update product!");
    }
  };

  return (
    <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
      {/* Product Name */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Product Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          {...register("name")}
          placeholder="Enter product name"
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none ${
            errors.name ? "border-red-500" : "border-gray-300"
          }`}
        />
        {errors.name && (
          <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
        )}
      </div>

      {/* Category */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Category <span className="text-red-500">*</span>
        </label>
        <select
          {...register("category_id")}
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none ${
            errors.category_id ? "border-red-500" : "border-gray-300"
          }`}>
          <option value="">Select category</option>
          {categories.map((cat) => (
            <option key={cat.id} value={cat.id}>
              {cat.name}
            </option>
          ))}
        </select>
        {errors.category_id && (
          <p className="text-red-500 text-sm mt-1">
            {errors.category_id.message}
          </p>
        )}
      </div>

      {/* Price */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Price <span className="text-red-500">*</span>
        </label>
        <input
          type="number"
          step="0.01"
          {...register("base_price")}
          placeholder="Enter price"
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none ${
            errors.base_price ? "border-red-500" : "border-gray-300"
          }`}
        />
        {errors.base_price && (
          <p className="text-red-500 text-sm mt-1">
            {errors.base_price.message}
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Stock Quantity <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            {...register("stock_quantity")}
            placeholder="Enter stock quantity"
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none ${
              errors.stock_quantity ? "border-red-500" : "border-gray-300"
            }`}
          />
          {errors.stock_quantity && (
            <p className="text-red-500 text-sm mt-1">
              {errors.stock_quantity.message}
            </p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Min Order Quantity <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            {...register("min_order_quantity")}
            placeholder="Enter min order quantity"
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none ${
              errors.min_order_quantity ? "border-red-500" : "border-gray-300"
            }`}
          />
          {errors.min_order_quantity && (
            <p className="text-red-500 text-sm mt-1">
              {errors.min_order_quantity.message}
            </p>
          )}
        </div>
      </div>

      <div className="mb-5">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Upload Image{" "}
          <span className="text-gray-400 font-medium">(Optional)</span>
        </label>
        <Controller
          control={control}
          name="files"
          render={({ field }) => (
            <div className="flex flex-col items-start gap-3">
              <label
                htmlFor="file-upload"
                className={`flex flex-col items-center justify-center w-full h-28 border-2 border-dashed rounded-2xl cursor-pointer transition-all duration-300 ${
                  errors.files
                    ? "border-red-400 bg-red-50/40"
                    : "border-gray-300 hover:border-blue-400 hover:bg-blue-50/40"
                }`}>
                <ImagePlus className="w-7 h-7 text-gray-500 mb-2" />
                <p className="text-sm text-gray-600">
                  Click to{" "}
                  <span className="font-semibold text-blue-500">upload</span>{" "}
                  new image
                </p>
                <input
                  id="file-upload"
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={(e) => {
                    field.onChange(e.target.files);
                    if (e.target.files && e.target.files[0]) {
                      setPreviewImage(URL.createObjectURL(e.target.files[0]));
                    }
                  }}
                  className="hidden"
                />
              </label>

              {previewImage && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                  className="relative">
                  <img
                    src={previewImage}
                    alt="preview"
                    className="w-40 h-40 object-cover rounded-xl shadow-md border border-gray-200"
                  />
                </motion.div>
              )}
            </div>
          )}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Description <span className="text-red-500">*</span>
        </label>
        <textarea
          {...register("description")}
          placeholder="Write product description"
          className={`w-full h-[100px] px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none ${
            errors.description ? "border-red-500" : "border-gray-300"
          }`}
        />
        {errors.description && (
          <p className="text-red-500 text-sm mt-1">
            {errors.description.message}
          </p>
        )}
      </div>

      <div className="flex gap-3 justify-end pt-2">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition">
          Cancel
        </button>
        <button
          type="submit"
          disabled={isUpdating}
          className={`px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition ${
            isUpdating ? "opacity-50 cursor-not-allowed" : ""
          }`}>
          {isUpdating ? "Updating..." : "Save Changes"}
        </button>
      </div>
    </form>
  );
};

export default EditProductForm;
